import React, { useEffect, useMemo, useState } from "react";
import { X, Send, Copy, Check, ShieldCheck, ExternalLink, Link as LinkIcon } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";

/**
 * SendToClientModal — Iter AF
 *
 * Therapist reviews & edits the AI-generated recommendations BEFORE sending.
 * Nothing is published until the therapist clicks "Approve & create link".
 * The backend stores ONLY the approved values — never the raw bundle.
 */
export default function SendToClientModal({ open, onClose, sessionId, bundle, client }) {
  const [draft, setDraft] = useState(null);
  const [therapistNote, setTherapistNote] = useState("");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (open && bundle) {
      setDraft({
        client_message: bundle.client_message || "",
        todays_session: bundle.todays_session || "",
        biggest_win: bundle.biggest_win || "",
        home_care: (bundle.home_care || []).join("\n"),
        stretches_text: (bundle.stretches || []).length
          ? bundle.stretches.map((s) => (s.dosage ? `${s.name} — ${s.dosage}` : s.name)).join("\n")
          : (bundle.suggested_stretches || []).join("\n"),
        next_visit: bundle.next_visit || "",
        progress_portal_content: bundle.progress_portal_content || "",
        qr_share_content: bundle.qr_share_content || "",
      });
      setTherapistNote("");
      setResult(null);
      setCopied(false);
    }
  }, [open, bundle]);

  const publicUrl = useMemo(() => {
    if (!result) return "";
    return `${window.location.origin}${result.public_path}`;
  }, [result]);

  if (!open) return null;
  if (!bundle) return null;

  const send = async () => {
    if (!sessionId) {
      toast.error("Save the session first.");
      return;
    }
    setSending(true);
    try {
      // Rebuild structured stretch cards from the "Name — dosage" lines,
      // preserving each stretch's how_to from the original bundle by name.
      const origByName = Object.fromEntries(
        (bundle.stretches || []).map((s) => [(s.name || "").toLowerCase(), s])
      );
      const stretches = (draft.stretches_text || "")
        .split("\n").map((s) => s.trim()).filter(Boolean)
        .map((line) => {
          const parts = line.includes(" — ") ? line.split(" — ") : line.split(" - ");
          const name = (parts[0] || line).trim();
          const orig = origByName[name.toLowerCase()] || {};
          return {
            name,
            dosage: (parts[1] || orig.dosage || "").trim(),
            how_to: orig.how_to || "",
          };
        });
      const payload = {
        client_message: draft.client_message,
        todays_session: draft.todays_session,
        biggest_win: draft.biggest_win,
        home_care: (draft.home_care || "").split("\n").map((s) => s.trim()).filter(Boolean),
        stretches,
        suggested_stretches: stretches.map((s) => s.name),
        next_visit: draft.next_visit,
        progress_portal_content: draft.progress_portal_content,
        qr_share_content: draft.qr_share_content,
        therapist_note: therapistNote || null,
      };
      const r = await api.post(`/portal/recommendations/${sessionId}`, payload);
      setResult(r.data);
      toast.success("Share link ready");
    } catch (e) {
      const detail = e?.response?.data?.detail || "Could not create the link.";
      toast.error(detail === "generate_recommendations_first" ? "Generate recommendations first." : detail);
    } finally {
      setSending(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopied(true);
      toast.success("Link copied");
      setTimeout(() => setCopied(false), 1500);
    } catch { toast.error("Copy failed"); }
  };

  const sendViaEmail = () => {
    if (!client?.email) {
      toast.error("This client has no email on file.");
      return;
    }
    const subject = encodeURIComponent("Your session recommendations");
    const body = encodeURIComponent(
      `Hi ${(client?.name || "").split(" ")[0]},\n\nHere are the recommendations from today's session:\n${publicUrl}\n`
    );
    window.location.href = `mailto:${client.email}?subject=${subject}&body=${body}`;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black/40" data-testid="send-to-client-modal">
      <div className="bg-paper-50 w-full max-w-2xl max-h-[90vh] rounded-3xl border border-paper-200 shadow-xl overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-paper-200 bg-white">
          <div>
            <div className="label-overline text-sage-700 mb-0.5">Review &amp; send to client</div>
            <h2 className="font-heading text-lg text-ink-900">Approve before sharing</h2>
          </div>
          <button type="button" onClick={onClose} data-testid="send-modal-close" className="p-2 rounded-full hover:bg-paper-100">
            <X className="w-5 h-5" />
          </button>
        </div>

        {!result ? (
          <div className="flex-1 overflow-y-auto p-5 sm:p-6 space-y-5" data-testid="send-modal-review">
            <div className="flex items-start gap-2.5 bg-amber-50 border border-amber-200 rounded-2xl p-3 text-sm text-ink-900">
              <ShieldCheck className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
              <div>
                <div className="font-medium">Therapist review required</div>
                <div className="text-xs text-ink-700 mt-0.5">Edit anything below. Only what you approve here will be sent to {client?.name || "the client"}.</div>
              </div>
            </div>

            <div>
              <label className="label-overline block mb-2">Optional personal note (shown at the top)</label>
              <textarea
                value={therapistNote}
                onChange={(e) => setTherapistNote(e.target.value)}
                data-testid="send-modal-therapist-note"
                rows={2}
                maxLength={600}
                placeholder='"Great work today — hope you feel lighter tomorrow."'
                className="w-full px-3 py-2 rounded-2xl bg-white border border-paper-200 text-sm"
              />
            </div>

            <div>
              <label className="label-overline block mb-2">Client message (SMS-ready)</label>
              <textarea
                value={draft?.client_message || ""}
                onChange={(e) => setDraft({ ...draft, client_message: e.target.value })}
                data-testid="send-modal-client-message"
                rows={4}
                className="w-full px-3 py-2 rounded-2xl bg-white border border-paper-200 text-sm"
              />
            </div>

            <div>
              <label className="label-overline block mb-2">Today&rsquo;s session (2-3 sentences the client sees first)</label>
              <textarea
                value={draft?.todays_session || ""}
                onChange={(e) => setDraft({ ...draft, todays_session: e.target.value })}
                data-testid="send-modal-todays-session"
                rows={3}
                className="w-full px-3 py-2 rounded-2xl bg-white border border-paper-200 text-sm"
              />
            </div>

            <div>
              <label className="label-overline block mb-2">Biggest win (one celebratory sentence)</label>
              <textarea
                value={draft?.biggest_win || ""}
                onChange={(e) => setDraft({ ...draft, biggest_win: e.target.value })}
                data-testid="send-modal-biggest-win"
                rows={2}
                className="w-full px-3 py-2 rounded-2xl bg-white border border-paper-200 text-sm"
              />
            </div>

            <div>
              <label className="label-overline block mb-2">Home care (one per line, max 4)</label>
              <textarea
                value={draft?.home_care || ""}
                onChange={(e) => setDraft({ ...draft, home_care: e.target.value })}
                data-testid="send-modal-home-care"
                rows={4}
                className="w-full px-3 py-2 rounded-2xl bg-white border border-paper-200 text-sm"
              />
            </div>

            <div>
              <label className="label-overline block mb-2">Stretches (one per line: Name — dosage)</label>
              <textarea
                value={draft?.stretches_text || ""}
                onChange={(e) => setDraft({ ...draft, stretches_text: e.target.value })}
                data-testid="send-modal-stretches"
                rows={3}
                placeholder={"Upper Trapezius Stretch — 30 sec × 3"}
                className="w-full px-3 py-2 rounded-2xl bg-white border border-paper-200 text-sm"
              />
              <p className="text-[11px] text-ink-500 mt-1">Instructions are shown to the client as tappable cards.</p>
            </div>

            <div>
              <label className="label-overline block mb-2">Next visit (one sentence)</label>
              <textarea
                value={draft?.next_visit || ""}
                onChange={(e) => setDraft({ ...draft, next_visit: e.target.value })}
                data-testid="send-modal-next-visit"
                rows={2}
                className="w-full px-3 py-2 rounded-2xl bg-white border border-paper-200 text-sm"
              />
            </div>

            {!!(draft?.progress_portal_content || "").trim() && !(draft?.todays_session || "").trim() && (
            <div>
              <label className="label-overline block mb-2">Progress portal content (long-form)</label>
              <textarea
                value={draft?.progress_portal_content || ""}
                onChange={(e) => setDraft({ ...draft, progress_portal_content: e.target.value })}
                data-testid="send-modal-portal"
                rows={6}
                className="w-full px-3 py-2 rounded-2xl bg-white border border-paper-200 text-sm"
              />
            </div>
            )}
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto p-5 sm:p-6 space-y-5" data-testid="send-modal-result">
            <div className="flex items-start gap-2.5 bg-sage-50 border border-sage-200 rounded-2xl p-3 text-sm text-ink-900">
              <Check className="w-5 h-5 text-sage-600 shrink-0 mt-0.5" />
              <div>
                <div className="font-medium">Approved and ready to share</div>
                <div className="text-xs text-ink-700 mt-0.5">This link will work for 30 days. You can revoke it anytime from the session view.</div>
              </div>
            </div>
            <div>
              <label className="label-overline block mb-2">Public link</label>
              <div className="flex items-center gap-2 flex-wrap">
                <input
                  readOnly
                  value={publicUrl}
                  data-testid="send-modal-public-url"
                  className="flex-1 min-w-[260px] px-3 py-2 rounded-full bg-white border border-paper-200 text-sm font-mono"
                />
                <button
                  type="button"
                  onClick={copy}
                  data-testid="send-modal-copy-url"
                  className="min-h-[40px] px-4 rounded-full bg-ink-900 text-white text-sm inline-flex items-center gap-1.5"
                >
                  {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                  {copied ? "Copied" : "Copy"}
                </button>
                <a
                  href={publicUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="send-modal-view-as-client"
                  className="min-h-[40px] px-4 rounded-full border border-paper-200 hover:bg-paper-100 text-sm inline-flex items-center gap-1.5 text-ink-900"
                >
                  <ExternalLink className="w-3.5 h-3.5" /> View as client
                </a>
              </div>
            </div>
            <div className="flex gap-2 flex-wrap">
              <button
                type="button"
                onClick={sendViaEmail}
                data-testid="send-modal-email-btn"
                className="min-h-[44px] px-5 rounded-full bg-sage-500 hover:bg-sage-600 text-white text-sm font-medium inline-flex items-center gap-1.5"
              >
                <Send className="w-4 h-4" /> Email link to client
              </button>
              <a
                href={`sms:${client?.phone || ""}?body=${encodeURIComponent(`Hi ${(client?.name||"").split(" ")[0]}, here are your session recommendations: ${publicUrl}`)}`}
                data-testid="send-modal-sms-btn"
                className="min-h-[44px] px-5 rounded-full bg-amber-500 hover:bg-amber-600 text-white text-sm font-medium inline-flex items-center gap-1.5"
              >
                <LinkIcon className="w-4 h-4" /> Text link
              </a>
            </div>
          </div>
        )}

        <div className="p-5 border-t border-paper-200 bg-white flex justify-end gap-2 flex-wrap">
          {!result ? (
            <>
              <button type="button" onClick={onClose} data-testid="send-modal-cancel" className="min-h-[44px] px-5 rounded-full text-ink-700 hover:bg-paper-100">
                Cancel
              </button>
              <button
                type="button"
                onClick={send}
                disabled={sending}
                data-testid="send-modal-approve-btn"
                className="min-h-[44px] px-6 rounded-full bg-ink-900 hover:bg-ink-800 text-white font-medium disabled:opacity-50 inline-flex items-center gap-2"
              >
                <ShieldCheck className="w-4 h-4" />
                {sending ? "Creating link…" : "Approve & create link"}
              </button>
            </>
          ) : (
            <button type="button" onClick={onClose} data-testid="send-modal-done" className="min-h-[44px] px-6 rounded-full bg-ink-900 hover:bg-ink-800 text-white font-medium">
              Done
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
